import React, { useState } from "react";
import { apiClient, formatApiError } from "../../lib/api";
import { Loader2, ShieldCheck, Trash2, X } from "lucide-react";

export const PaymentsCleanupPanel = ({ ar, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const run = async (fn) => {
    setBusy(true); setErr(null);
    try { await fn(); } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  const openPreview = () => run(async () => {
    setResult(null);
    const { data } = await apiClient.post("/payments/moyasar/purge-abandoned", null, { params: { dry_run: 1 } });
    setPreview(data);
  });
  const execute = () => run(async () => {
    const { data } = await apiClient.post("/payments/moyasar/purge-abandoned", null, { params: { confirm_token: preview.confirm_token } });
    setResult(data); setPreview(null); onDone?.();
  });

  return (
    <div className="mb-5" data-testid="payments-cleanup-panel">
      <button onClick={openPreview} disabled={busy}
        className="inline-flex items-center gap-2 text-xs rounded-full px-3 py-1.5 font-bold border border-red-400 text-red-700 hover:bg-red-50 disabled:opacity-50"
        data-testid="purge-abandoned-btn">
        {busy && !preview ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
        {ar ? "حذف المدفوعات المهجورة (معاينة أولاً)" : "Delete abandoned payments (preview first)"}
      </button>
      {err && <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="cleanup-error">{err}</div>}
      {result && <CleanupResult ar={ar} r={result} onClose={() => setResult(null)} />}
      {preview && <CleanupPreview ar={ar} p={preview} busy={busy} onCancel={() => setPreview(null)} onConfirm={execute} />}
    </div>
  );
};

function CleanupPreview({ ar, p, busy, onCancel, onConfirm }) {
  const keep = p.by_reason.filter((x) => !x.eligible);
  const del = p.by_reason.filter((x) => x.eligible);
  return (
    <div className="mt-3 rounded-2xl border border-[#0a0a0a]/10 bg-white p-5 space-y-4" data-testid="cleanup-preview">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display text-lg">{ar ? "معاينة التنظيف" : "Cleanup preview"}</div>
          <div className="text-xs text-[#6b6b6b]">
            {ar ? `مدة الاحتفاظ: ${p.retention_days} يوماً — لا يُحذف أي سجل أحدث من ${p.cutoff.slice(0, 10)}. تم التحقق من ميسر لكل سجل غير محسوم.`
                : `Retention: ${p.retention_days} days — nothing newer than ${p.cutoff.slice(0, 10)}. Moyasar checked for every unresolved record.`}
          </div>
        </div>
        <button onClick={onCancel} className="text-[#6b6b6b] hover:text-[#0a0a0a]" data-testid="cleanup-cancel-x" aria-label="close"><X className="w-4 h-4" /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <ReasonList title={ar ? `مؤهلة للحذف (${p.eligible})` : `Eligible (${p.eligible})`} items={del} tone="red" testid="cleanup-eligible" />
        <ReasonList title={ar ? `مستثناة (${p.kept})` : `Kept (${p.kept})`} items={keep} tone="green" testid="cleanup-kept" />
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="rounded-full px-4 py-2 text-sm border border-[#0a0a0a]/15" data-testid="cleanup-cancel">{ar ? "إلغاء" : "Cancel"}</button>
        <button onClick={onConfirm} disabled={busy || p.eligible === 0}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold bg-red-600 text-white disabled:opacity-40" data-testid="cleanup-confirm">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {ar ? `تأكيد حذف ${p.eligible} سجل` : `Confirm deleting ${p.eligible}`}
        </button>
      </div>
    </div>
  );
}

function ReasonList({ title, items, tone, testid }) {
  const c = tone === "red" ? "border-red-200 bg-red-50/50" : "border-emerald-200 bg-emerald-50/50";
  return (
    <div className={`rounded-xl border p-3 ${c}`} data-testid={testid}>
      <div className="text-sm font-bold mb-2">{title}</div>
      {items.length === 0 ? <div className="text-xs text-[#6b6b6b]">—</div> : items.map((x) => (
        <div key={x.code} className="flex justify-between gap-3 text-xs py-0.5" data-testid={`${testid}-${x.code}`}>
          <span>{x.reason}</span><span className="font-mono font-bold">{x.count}</span>
        </div>
      ))}
    </div>
  );
}

function CleanupResult({ ar, r, onClose }) {
  return (
    <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 flex items-start justify-between gap-3" data-testid="cleanup-result">
      <div className="flex gap-2"><ShieldCheck className="w-4 h-4 mt-0.5" />
        {ar ? `تم: حُذف ${r.deleted_total} سجل، واستُثني ${r.kept_total}${r.synced_paid_ids.length ? `، وزُومن ${r.synced_paid_ids.length} سجل مدفوع لدى ميسر` : ""}. سُجّلت العملية في سجل التدقيق.`
            : `Done: deleted ${r.deleted_total}, kept ${r.kept_total}${r.synced_paid_ids.length ? `, synced ${r.synced_paid_ids.length} paid` : ""}. Logged to audit.`}
      </div>
      <button onClick={onClose} aria-label="close" data-testid="cleanup-result-close"><X className="w-4 h-4" /></button>
    </div>
  );
}
